import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../api/config';
import TransferSizeMatrix, { totalQty } from './TransferSizeMatrix';
import './WarehouseAllocationPanel.css';
import WarehouseTransferA4Preview from './WarehouseTransferA4Preview';

const dateText = value => value ? new Date(value).toLocaleString() : '-';
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('erpWarehouseTransferToken') || ''}` });
export default function WarehouseAllocationPanel({ user, selectedLocation, onOpenTransfer }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [executionEnabled, setExecutionEnabled] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [route, setRoute] = useState('ALL');
  const [printPlanId, setPrintPlanId] = useState('');
  const [saving, setSaving] = useState('');
  const plans = useMemo(() => (run?.plans || []).filter(plan => route === 'ALL' || plan.routeType === route), [run, route]);
  const printPlan = run?.plans?.find(plan => plan.id === printPlanId);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setRun(null); setPrintPlanId('');
    fetch(`${API_BASE}/api/erp/warehouse/recommendations/latest?warehouse=${encodeURIComponent(selectedLocation || 'ALL')}`, { cache: 'no-store' })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) throw new Error(data.message || 'Summary load failed');
        if (active) { setRun(data.run || null); setExecutionEnabled(!!data.executionEnabled); }
      }).catch(err => { if (active) setError(err.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedLocation]);


  async function generate() {
    if (generating || saving || loading) return;
    setGenerating(true); setError(''); setNotice('');
    try {
      const response = await fetch(`${API_BASE}/api/erp/warehouse/recommendations/generate`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ source: 'MANUAL', username: user?.username, warehouse: selectedLocation || 'ALL', stockScope: 'TRANSFER_TEST' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.message || 'Summary generation failed');
      setRun(data.run); setExecutionEnabled(!!data.executionEnabled);
      const runId = data.run?.id;
      if (!runId) throw new Error('Summary run ID is missing.');
      if (!data.run.plans?.length) {
        setNotice('လက်ရှိ stock အရ ပို့ရန် transfer အကြံပြုချက် မရှိပါ။');
        return;
      }
      const aiResponse = await fetch(`${API_BASE}/api/erp/warehouse/recommendations/${encodeURIComponent(runId)}/request-ai`, {
        method: 'POST', headers: headers(), body: '{}',
      });
      const aiData = await aiResponse.json().catch(() => ({}));
      if (!aiResponse.ok || aiData.success === false) throw new Error(aiData.message || 'n8n AI summary could not start.');
      if (aiData.completed) {
        setNotice('n8n AI ရှင်းလင်းချက် ပြည့်စုံပါပြီ။');
        return;
      }
      let complete = false;
      for (let attempt = 0; attempt < 45; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const statusResponse = await fetch(`${API_BASE}/api/erp/warehouse/recommendations/${encodeURIComponent(runId)}`, {
          headers: headers(), cache: 'no-store',
        });
        const status = await statusResponse.json().catch(() => ({}));
        if (!statusResponse.ok || status.success === false) throw new Error(status.message || 'AI summary status check failed.');
        if (status.run) setRun(status.run);
        if (status.run?.plans?.length && status.run.plans.every(plan => !!plan.aiSummary)) {
          complete = true;
          break;
        }
      }
      if (!complete) throw new Error('n8n AI ရှင်းလင်းချက်ကို အချိန်မီ မရပါ။ စာရင်းကို ထုတ်ပြီးသားဖြစ်ပါသည်။');
      setNotice('n8n AI ရှင်းလင်းချက် ပြည့်စုံပါပြီ။');
    } catch (err) { setError(err.message); } finally { setGenerating(false); }
  }

  async function save(plan) {
    if (saving || generating) return;
    setSaving(plan.id); setError(''); setNotice('');
    try {
      const response = await fetch(`${API_BASE}/api/erp/warehouse/plans/${encodeURIComponent(plan.id)}/save`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ username: user?.username, version: plan.version,
          items: plan.items.map(item => ({ itemId: item.id, productCode: item.productCode, color: item.color, size: item.size, qty: item.qty })) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.message || 'Transfer save failed');
      setRun(previous => ({ ...previous, plans: previous.plans.map(row => row.id === plan.id ? { ...row, status: data.status, version: data.version, aiSummary: null } : row) }));
      setNotice(`${plan.fromLocationCode} → ${plan.toLocationCode} ကို Product Transfer ထဲ သိမ်းပြီးပါပြီ။ အဲဒီနေရာမှာ product / qty ပြင်ပြီး scan စစ်နိုင်ပါပြီ။`);
      onOpenTransfer?.(plan.id);
    } catch (err) { setError(err.message); } finally { setSaving(''); }
  }

  const meta = plan => <div className="warehouseAllocation__printMeta">
    <span><b>From</b> {plan.fromLocationCode}</span><span><b>To</b> {plan.toLocationCode}</span><span><b>Date</b> {dateText(run?.generatedAt)}</span>
    <span><b>Transfer ID</b> {plan.id}</span><span><b>Version</b> {plan.version}</span><span><b>Status</b> {plan.status}</span>
  </div>;

  return <section className="warehouseAllocation">
    <div className="warehouseAllocation__header"><div><span className="warehouseAllocation__eyebrow">WAREHOUSE / TRANSFER PLANNING</span><h2>Daily Transfer Summary</h2>
      <p>အကြံပြုစာရင်းကို ကြည့်ပြီး Save လုပ်ပါ။ Product Transfer မှာ product / qty ကို ပြင်ပြီး ပစ္စည်း scan စစ်နိုင်ပါသည်။</p></div>
      <button type="button" className="warehouseAllocation__generate" disabled={generating || loading || !!saving} onClick={generate}>{generating ? <><span className="warehouseAllocation__spinner" /> Waiting · Generating summary…</> : 'Generate Summary Now'}</button>
    </div>
    {error && <div className="warehouseAllocation__error" role="alert">{error}</div>}
    {notice && <div className="warehouseAllocation__loading" role="status">{notice}</div>}
    {loading && <div className="warehouseAllocation__loading">Loading latest summary…</div>}
    {generating && <div className="warehouseAllocation__loading" role="status">Stock စာရင်းနှင့် n8n AI အကြံပြုရှင်းလင်းချက် ထုတ်နေပါသည်။ ခဏစောင့်ပါ…</div>}
    {!loading && !run && !error && <div className="warehouseAllocation__empty">Summary မထုတ်ရသေးပါ။ Generate Summary Now ကိုနှိပ်ပါ။</div>}
    {run && <>
      {!executionEnabled && <div className="warehouseAllocation__loading">Summary ကြည့်နိုင်ပါသည်။ Live transfer Save ကို မဖွင့်ရသေးပါ။</div>}
      <div className="warehouseAllocation__meta"><span><b>Generated</b> {dateText(run.generatedAt)}</span><span><b>Snapshot</b> {dateText(run.stockSnapshotAt)}</span><span><b>Plans</b> {run.plans?.length || 0}</span><span>Read-only summary</span></div>
      <div className="warehouseAllocation__filters" aria-label="Transfer route filter">{[['ALL','All routes'],['WAREHOUSE_TO_SHOP','WS → Shop'],['SHOP_TO_SHOP','Shop → Shop']].map(([value,label]) => <button key={value} type="button" className={route === value ? 'isActive' : ''} onClick={() => setRoute(value)}>{label}</button>)}</div>
      <div className="warehouseAllocation__plans">
        {!plans.length && <div className="warehouseAllocation__empty">ဒီ snapshot မှာ ပို့ရန်အကြံပြုချက်မရှိပါ။</div>}
        {plans.map(plan => <details className="warehouseAllocation__plan" key={plan.id}>
          <summary><span className="warehouseAllocation__route">{plan.fromLocationCode} <span>→</span> {plan.toLocationCode}</span><span className="warehouseAllocation__badge">{plan.routeType === 'SHOP_TO_SHOP' ? 'Shop → Shop' : 'WS → Shop'}</span><span>{plan.items.length} SKU · {totalQty(plan)} pcs</span><span className="warehouseAllocation__status">{plan.status}</span></summary>
          <div className="warehouseAllocation__planBody">
            <div className="warehouseAllocation__planTools"><small>စာရင်းပြင်ရန် Product Transfer သို့သွားပါ။</small><div className="warehouseAllocation__planButtons">
              {plan.status === 'SUGGESTED' && <button type="button" disabled={!executionEnabled || !!saving || generating} onClick={() => save(plan)}>{saving === plan.id ? 'Rechecking stock…' : 'Save → Product Transfer'}</button>}
              {plan.status === 'READY' && <button type="button" onClick={() => onOpenTransfer?.(plan.id)}>Open Product Transfer</button>}
              <button type="button" onClick={() => setPrintPlanId(plan.id)}>Preview Transfer + Summary · A4</button>
            </div></div>
            {meta(plan)}
            <div className="warehouseAllocation__scroll warehouseAllocation__summaryMatrix"><TransferSizeMatrix plan={plan} /></div>
            {plan.aiSummary && <div className="warehouseAllocation__aiSummary"><strong>အကြံပြုရှင်းလင်းချက်</strong><p>{plan.aiSummary}</p></div>}
          </div>
        </details>)}
      </div>
    </>}
    {printPlan && <WarehouseTransferA4Preview plan={{ ...printPlan, generatedAt: run.generatedAt }} onClose={() => setPrintPlanId('')} />}
  </section>;
}

